/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListAgentConnectionBlocksRequest,
  ListAgentConnectionBlocksSecurity,
} from "../models/operations/listagentconnectionblocks.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AgentConnectionBlocksInfiniteQueryData,
  AgentConnectionBlocksPageParams,
  AgentConnectionBlocksQueryData,
  buildAgentConnectionBlocksInfiniteQuery,
  buildAgentConnectionBlocksQuery,
  prefetchAgentConnectionBlocks,
  prefetchAgentConnectionBlocksInfinite,
  queryKeyAgentConnectionBlocks,
  queryKeyAgentConnectionBlocksInfinite,
} from "./agentConnectionBlocks.core.js";
export {
  type AgentConnectionBlocksInfiniteQueryData,
  type AgentConnectionBlocksPageParams,
  type AgentConnectionBlocksQueryData,
  buildAgentConnectionBlocksInfiniteQuery,
  buildAgentConnectionBlocksQuery,
  prefetchAgentConnectionBlocks,
  prefetchAgentConnectionBlocksInfinite,
  queryKeyAgentConnectionBlocks,
  queryKeyAgentConnectionBlocksInfinite,
};

export type AgentConnectionBlocksQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listConnectionBlocks agents
 *
 * @remarks
 * List the agents the caller blocked from their own connections. Needs no agent management access.
 */
export function useAgentConnectionBlocks(
  request?: ListAgentConnectionBlocksRequest | undefined,
  security?: ListAgentConnectionBlocksSecurity | undefined,
  options?: QueryHookOptions<
    AgentConnectionBlocksQueryData,
    AgentConnectionBlocksQueryError
  >,
): UseQueryResult<
  AgentConnectionBlocksQueryData,
  AgentConnectionBlocksQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildAgentConnectionBlocksQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listConnectionBlocks agents
 *
 * @remarks
 * List the agents the caller blocked from their own connections. Needs no agent management access.
 */
export function useAgentConnectionBlocksSuspense(
  request?: ListAgentConnectionBlocksRequest | undefined,
  security?: ListAgentConnectionBlocksSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AgentConnectionBlocksQueryData,
    AgentConnectionBlocksQueryError
  >,
): UseSuspenseQueryResult<
  AgentConnectionBlocksQueryData,
  AgentConnectionBlocksQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAgentConnectionBlocksQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listConnectionBlocks agents
 *
 * @remarks
 * List the agents the caller blocked from their own connections. Needs no agent management access.
 */
export function useAgentConnectionBlocksInfinite(
  request?: ListAgentConnectionBlocksRequest | undefined,
  security?: ListAgentConnectionBlocksSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    AgentConnectionBlocksInfiniteQueryData,
    AgentConnectionBlocksQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    AgentConnectionBlocksInfiniteQueryData,
    AgentConnectionBlocksPageParams
  >,
  AgentConnectionBlocksQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    AgentConnectionBlocksInfiniteQueryData,
    AgentConnectionBlocksQueryError,
    InfiniteData<
      AgentConnectionBlocksInfiniteQueryData,
      AgentConnectionBlocksPageParams
    >,
    QueryKey,
    AgentConnectionBlocksPageParams
  >({
    ...buildAgentConnectionBlocksInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listConnectionBlocks agents
 *
 * @remarks
 * List the agents the caller blocked from their own connections. Needs no agent management access.
 */
export function useAgentConnectionBlocksInfiniteSuspense(
  request?: ListAgentConnectionBlocksRequest | undefined,
  security?: ListAgentConnectionBlocksSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    AgentConnectionBlocksInfiniteQueryData,
    AgentConnectionBlocksQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    AgentConnectionBlocksInfiniteQueryData,
    AgentConnectionBlocksPageParams
  >,
  AgentConnectionBlocksQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    AgentConnectionBlocksInfiniteQueryData,
    AgentConnectionBlocksQueryError,
    InfiniteData<
      AgentConnectionBlocksInfiniteQueryData,
      AgentConnectionBlocksPageParams
    >,
    QueryKey,
    AgentConnectionBlocksPageParams
  >({
    ...buildAgentConnectionBlocksInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setAgentConnectionBlocksData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: AgentConnectionBlocksQueryData,
): AgentConnectionBlocksQueryData | undefined {
  const key = queryKeyAgentConnectionBlocks(...queryKeyBase);

  return client.setQueryData<AgentConnectionBlocksQueryData>(key, data);
}

export function invalidateAgentConnectionBlocks(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "agents",
      "listConnectionBlocks",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllAgentConnectionBlocks(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "agents", "listConnectionBlocks"],
  });
}
